import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtBRL, fmtNumber, fmtPct, fmtDateShort } from "../lib/format";
import { computeDelta } from "../lib/delta";
import { useFilters, periodToDates, periodLabel as fmtPeriodLabel } from "../lib/filters";
import { KPIStatCard } from "../components/KPIStatCard";
import { ChartCard } from "../components/ChartCard";
import { PageHeader } from "../components/PageHeader";
import { LoadMoreBar, useProgressiveList } from "../components/LoadMoreRow";
import {
  IconAlertTriangle, IconLightbulb, IconInfo, IconArrowUp, IconArrowDown, IconMinus,
} from "../components/icons";
import type {
  TransportOverviewDto,
  TransportCteDashboardDto,
  TransportFinancialDto,
} from "@bi/types";

// ── tipos locais ──────────────────────────────────────────────────────────────

type Severity = "critical" | "warning" | "info";

interface AutoAlert {
  severity: Severity;
  title: string;
  description: string;
  action: string;
}

interface AutoInsight {
  title: string;
  body: string;
  metric: string;
  trend: "up" | "down" | "flat";
}

// ── helpers ───────────────────────────────────────────────────────────────────

function pct(current: number, prev: number): number | null {
  if (prev === 0) return null;
  return ((current - prev) / Math.abs(prev)) * 100;
}

function TrendIcon({ trend }: { trend: "up" | "down" | "flat" }) {
  if (trend === "up")   return <IconArrowUp   className="w-3.5 h-3.5" />;
  if (trend === "down") return <IconArrowDown className="w-3.5 h-3.5" />;
  return <IconMinus className="w-3.5 h-3.5" />;
}

const SEVERITY_STYLES: Record<Severity, { dot: string; badge: string; bg: string; border: string; title: string }> = {
  critical: { dot: "bg-danger-600", badge: "Alta",   bg: "bg-danger-50",  border: "border-danger-200",  title: "text-danger-800"  },
  warning:  { dot: "bg-warning-500", badge: "Média", bg: "bg-warning-50", border: "border-warning-200", title: "text-warning-800" },
  info:     { dot: "bg-info-500",    badge: "Baixa", bg: "bg-blue-50",    border: "border-blue-200",    title: "text-blue-800"    },
};

// ── Page ──────────────────────────────────────────────────────────────────────

export function ExecutivePage() {
  const { user } = useAuth();
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ["transport", "overview", from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportOverviewDto>(
        `/dashboards/transport/overview?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ).catch(() => null),
    staleTime: 60_000,
  });

  const { data: cte, isLoading: cteLoading } = useQuery({
    queryKey: ["transport", "cte", from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportCteDashboardDto>(
        `/dashboards/transport/cte?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ).catch(() => null),
    staleTime: 60_000,
  });

  const { data: fin, isLoading: finLoading } = useQuery({
    queryKey: ["transport", "financial", from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportFinancialDto>(
        `/dashboards/transport/financial?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ).catch(() => null),
    staleTime: 60_000,
  });

  const isLoading = overviewLoading || cteLoading || finLoading;

  // ── Escalares do resumo CTE ───────────────────────────────────────────────
  const s = cte?.summary;
  const p = cte?.prevSummary;

  // ── Deltas ────────────────────────────────────────────────────────────────
  const revDelta       = computeDelta(s?.totalValue,  p?.totalValue,  { direction: "higher_is_better" });
  const countDelta     = computeDelta(s?.count,       p?.count,       { direction: "higher_is_better" });
  const ticketDelta    = computeDelta(s?.avgTicket,   p?.avgTicket,   { direction: "higher_is_better" });
  const receivedDelta  = computeDelta(fin?.receivedMtd, fin?.receivedPrevMtd, { direction: "higher_is_better" });
  const paidDelta      = computeDelta(fin?.paidMtd,   fin?.paidPrevMtd,   { direction: "higher_is_worse" });
  const arOverdueDelta = computeDelta(fin?.arOverdueTotal, fin?.arOverduePrev1M, { direction: "higher_is_worse" });

  // ── Alertas automáticos (derivados de dados reais) ────────────────────────
  const computedAlerts = useMemo((): AutoAlert[] => {
    const list: AutoAlert[] = [];

    if (s && p && p.totalValue > 100) {
      const revPct = pct(s.totalValue, p.totalValue);
      if (revPct !== null && revPct < -10) {
        list.push({
          severity: "warning",
          title: "Queda no faturamento",
          description: `Faturamento ${Math.abs(revPct).toFixed(1)}% abaixo do ${cte?.comparisonLabel ?? "período anterior"} (${fmtBRL(s.totalValue)} vs. ${fmtBRL(p.totalValue)}).`,
          action: "Verificar carteira de tomadores e volume de operações.",
        });
      }
    }

    if (s && p && p.count > 0) {
      const cntPct = pct(s.count, p.count);
      if (cntPct !== null && cntPct < -15) {
        list.push({
          severity: "warning",
          title: "Queda no volume de CT-es",
          description: `Apenas ${fmtNumber(s.count)} CT-es no período — ${Math.abs(cntPct).toFixed(1)}% a menos que o ${cte?.comparisonLabel ?? "período anterior"} (${fmtNumber(p.count)}).`,
          action: "Verificar emissões pendentes e operações em andamento.",
        });
      }
    }

    if (s && p && p.avgTicket > 100) {
      const tktPct = pct(s.avgTicket, p.avgTicket);
      if (tktPct !== null && tktPct < -8) {
        list.push({
          severity: "info",
          title: "Queda no ticket médio",
          description: `Ticket médio de ${fmtBRL(s.avgTicket)} — ${Math.abs(tktPct).toFixed(1)}% abaixo do período anterior (${fmtBRL(p.avgTicket)}).`,
          action: "Analisar mix de cargas e distâncias médias.",
        });
      }
    }

    if (fin?.arOverdueTotal && fin.arOverdueTotal > 50_000) {
      list.push({
        severity: fin.arOverdueTotal > 200_000 ? "critical" : "warning",
        title: "Inadimplência elevada",
        description: `${fmtBRL(fin.arOverdueTotal)} em contas a receber vencidas.${fin.arOpenTotal > 0 ? ` Representa ${fmtPct((fin.arOverdueTotal / fin.arOpenTotal) * 100)} do total em aberto.` : ""}`,
        action: "Acionar cobrança dos maiores inadimplentes identificados abaixo.",
      });
    }

    if (fin?.cashBalance !== undefined && fin.cashBalance < 0) {
      list.push({
        severity: "critical",
        title: "Saldo bancário negativo",
        description: `Saldo atual: ${fmtBRL(fin.cashBalance)}. Atenção imediata necessária.`,
        action: "Avaliar antecipação de recebíveis ou linhas de crédito.",
      });
    }

    if (overview?.topCustomers && overview.topCustomers.length > 0) {
      const total = overview.topCustomers.reduce((acc, c) => acc + c.totalValue, 0);
      const top1  = overview.topCustomers[0];
      if (top1 && total > 0) {
        const share = (top1.totalValue / total) * 100;
        if (share > 35) {
          list.push({
            severity: "info",
            title: "Concentração de faturamento",
            description: `"${top1.name}" representa ${share.toFixed(1)}% do faturamento do período (${fmtBRL(top1.totalValue)}).`,
            action: "Diversificar carteira para reduzir dependência de cliente único.",
          });
        }
      }
    }

    return list;
  }, [s, p, fin, overview, cte]);

  // ── Insights automáticos ─────────────────────────────────────────────────
  const computedInsights = useMemo((): AutoInsight[] => {
    const list: AutoInsight[] = [];

    if (s && p && p.totalValue > 100) {
      const revPct = pct(s.totalValue, p.totalValue);
      if (revPct !== null) {
        const dir = revPct > 0 ? "acima" : "abaixo";
        list.push({
          title: `Faturamento ${revPct > 0 ? "em alta" : "em queda"}`,
          body: `Receita CT-e de ${fmtBRL(s.totalValue)} — ${Math.abs(revPct).toFixed(1)}% ${dir} do ${cte?.comparisonLabel ?? "período anterior"}.`,
          metric: fmtBRL(s.totalValue),
          trend: revPct > 1 ? "up" : revPct < -1 ? "down" : "flat",
        });
      }
    }

    if (s && s.count > 0) {
      list.push({
        title: "Volume de CT-es",
        body: `${fmtNumber(s.count)} CT-es autorizados no período, com ticket médio de ${fmtBRL(s.avgTicket)}.`,
        metric: fmtNumber(s.count),
        trend: "flat",
      });
    }

    if (overview?.topCustomers && overview.topCustomers.length >= 3) {
      const top3Total = overview.topCustomers.slice(0, 3).reduce((a, c) => a + c.totalValue, 0);
      const total     = overview.topCustomers.reduce((a, c) => a + c.totalValue, 0);
      const pct3 = total > 0 ? (top3Total / total) * 100 : 0;
      const leader = overview.topCustomers[0];
      list.push({
        title: "Concentração por tomador",
        body: `Os 3 maiores tomadores respondem por ${pct3.toFixed(1)}% do faturamento.${leader ? ` Líder: "${leader.name}" (${leader.formatted}).` : ""}`,
        metric: `${pct3.toFixed(1)}%`,
        trend: pct3 > 60 ? "down" : "flat",
      });
    }

    if (fin?.arOpenTotal && fin.arOpenTotal > 0) {
      const overduePct = fin.arOverdueTotal / fin.arOpenTotal * 100;
      list.push({
        title: "Posição de recebíveis",
        body: `Total a receber em aberto: ${fmtBRL(fin.arOpenTotal)}.${overduePct > 0.5 ? ` Vencido: ${fmtPct(overduePct)} (${fmtBRL(fin.arOverdueTotal)}).` : " Sem inadimplência identificada."}`,
        metric: fmtBRL(fin.arOpenTotal),
        trend: overduePct > 20 ? "down" : overduePct > 0 ? "flat" : "up",
      });
    }

    if (s && p && p.avgTicket > 100) {
      const tktPct = pct(s.avgTicket, p.avgTicket);
      if (tktPct !== null && Math.abs(tktPct) > 2) {
        list.push({
          title: `Ticket médio ${tktPct > 0 ? "em alta" : "em queda"}`,
          body: `Ticket médio atual de ${fmtBRL(s.avgTicket)} — ${Math.abs(tktPct).toFixed(1)}% ${tktPct > 0 ? "acima" : "abaixo"} do período anterior (${fmtBRL(p.avgTicket)}).`,
          metric: fmtBRL(s.avgTicket),
          trend: tktPct > 1 ? "up" : tktPct < -1 ? "down" : "flat",
        });
      }
    }

    return list;
  }, [s, p, fin, overview, cte]);

  // ── Dados dos gráficos ────────────────────────────────────────────────────
  const customerList = useProgressiveList(overview?.topCustomers ?? []);
  const debtorList   = useProgressiveList(fin?.topDebtors ?? []);

  const dailyChartData = (overview?.cteByDay ?? []).map((r) => ({
    date: r.date,
    faturamento: r.totalValue,
  }));

  const flowChartData = (fin?.monthlyFlow ?? []).map((r) => ({
    month: r.month,
    receber: r.received,
    pagar:   r.paid,
  }));

  // ── Period display ────────────────────────────────────────────────────────
  const fmtPDate = (iso: string) =>
    new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      timeZone: "America/Sao_Paulo",
    });
  const periodDisplay = `${fmtPDate(from)} → ${fmtPDate(new Date(new Date(to).getTime() - 1).toISOString())}`;

  const clientesAtivos = cte?.summary.activeCustomers ?? 0;

  return (
    <div className="space-y-6">

      {/* ── Cabeçalho ──────────────────────────────────────────────────────── */}
      <PageHeader
        title="Visão Executiva"
        subtitle={`Saúde operacional e financeira da ${user?.isDemo && user.companyName ? user.companyName : "Transmasi"}`}
      />

      {/* ── Banner executivo — paleta Transmasi ──────────────────────────── */}
      <div className="rounded-xl bg-gradient-to-br from-tm-800 to-tm-700 text-white p-6 shadow-lg">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <p className="text-[11px] font-semibold text-tm-200 uppercase tracking-widest">Período analisado</p>
            <p className="text-base font-semibold text-white mt-0.5">{periodDisplay}</p>
          </div>
          <span className="px-3 py-1.5 rounded-full bg-white/15 text-tm-100 text-xs font-medium">
            {fmtPeriodLabel(filters.period)}
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
          <div className="border-l-2 border-white/20 pl-4">
            <p className="text-xs text-tm-100/70 mb-1">Faturamento CT-e</p>
            <p className="text-xl font-bold text-white">
              {isLoading ? "—" : fmtBRL(s?.totalValue ?? 0)}
            </p>
            {s && p && p.totalValue > 0 && (
              <p className="text-xs text-tm-200 mt-0.5">
                vs. {fmtBRL(p.totalValue)}
              </p>
            )}
          </div>

          <div className="border-l-2 border-white/20 pl-4">
            <p className="text-xs text-tm-100/70 mb-1">CT-es autorizados</p>
            <p className="text-xl font-bold text-white">
              {isLoading ? "—" : fmtNumber(s?.count ?? 0)}
            </p>
            {s && p && p.count > 0 && (
              <p className="text-xs text-tm-200 mt-0.5">
                vs. {fmtNumber(p.count)}
              </p>
            )}
          </div>

          <div className="border-l-2 border-white/20 pl-4">
            <p className="text-xs text-tm-100/70 mb-1">Ticket médio</p>
            <p className="text-xl font-bold text-white">
              {isLoading ? "—" : fmtBRL(s?.avgTicket ?? 0)}
            </p>
            {s && p && p.avgTicket > 0 && (
              <p className="text-xs text-tm-200 mt-0.5">
                vs. {fmtBRL(p.avgTicket)}
              </p>
            )}
          </div>

          <div className="border-l-2 border-white/40 pl-4">
            <p className="text-xs text-tm-100/70 mb-1">
              A receber em aberto
            </p>
            <p className="text-xl font-bold text-white">
              {isLoading ? "—" : fmtBRL(fin?.arOpenTotal ?? 0)}
            </p>
            {fin && fin.arOverdueTotal > 0 && (
              <p className="text-xs text-red-300 mt-0.5">
                {fmtBRL(fin.arOverdueTotal)} vencido
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── Alertas automáticos ────────────────────────────────────────────── */}
      {computedAlerts.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
            <IconAlertTriangle className="w-3.5 h-3.5 text-warning-500" />
            Alertas do período
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {computedAlerts.map((alert, i) => {
              const st = SEVERITY_STYLES[alert.severity];
              return (
                <div
                  key={i}
                  className={`rounded-xl border px-4 py-3.5 ${st.bg} ${st.border}`}
                >
                  <div className="flex items-start gap-2.5">
                    <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${st.dot}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className={`text-sm font-semibold ${st.title}`}>{alert.title}</p>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-md ${st.bg} border ${st.border} ${st.title}`}>
                          {st.badge}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 leading-relaxed">{alert.description}</p>
                      <p className="text-xs text-gray-500 mt-1.5 italic">{alert.action}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── KPI Cards ─────────────────────────────────────────────────────── */}
      <div>
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3">
          Indicadores do período
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <KPIStatCard
            label="Faturamento CT-e"
            value={s ? fmtBRL(s.totalValue) : "—"}
            delta={revDelta}
            comparisonLabel={cte?.comparisonLabel ?? null}
            status="good"
            loading={isLoading}
          />
          <KPIStatCard
            label="CT-es emitidos"
            value={s ? fmtNumber(s.count) : "—"}
            delta={countDelta}
            comparisonLabel={cte?.comparisonLabel ?? null}
            status="good"
            loading={isLoading}
          />
          <KPIStatCard
            label="Ticket médio CT-e"
            value={s ? fmtBRL(s.avgTicket) : "—"}
            delta={ticketDelta}
            comparisonLabel={cte?.comparisonLabel ?? null}
            status="good"
            loading={isLoading}
          />
          <KPIStatCard
            label="Tomadores ativos"
            value={s ? fmtNumber(clientesAtivos) : "—"}
            delta={null}
            comparisonLabel="clientes com CT-e autorizado"
            status="neutral"
            loading={isLoading}
          />
          <KPIStatCard
            label="Recebido no período"
            value={fin ? fmtBRL(fin.receivedMtd) : "—"}
            delta={receivedDelta}
            comparisonLabel={fin ? "vs. período anterior" : null}
            status="good"
            loading={isLoading}
          />
          <KPIStatCard
            label="Pago no período"
            value={fin ? fmtBRL(fin.paidMtd) : "—"}
            delta={paidDelta}
            comparisonLabel={fin ? "vs. período anterior" : null}
            status="neutral"
            loading={isLoading}
          />
          <KPIStatCard
            label="A receber em aberto"
            value={fin ? fmtBRL(fin.arOpenTotal) : "—"}
            delta={null}
            comparisonLabel="posição atual"
            status="good"
            loading={isLoading}
          />
          <KPIStatCard
            label="Inadimplência"
            value={fin ? fmtBRL(fin.arOverdueTotal) : "—"}
            delta={arOverdueDelta}
            comparisonLabel={fin ? "vs. mesmo dia 1 mês atrás" : null}
            status={fin ? (fin.arOverdueTotal > 100_000 ? "critical" : fin.arOverdueTotal > 0 ? "warning" : "good") : "neutral"}
            loading={isLoading}
          />
        </div>
      </div>

      {/* ── Insights automáticos ────────────────────────────────────────────── */}
      {computedInsights.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
            <IconLightbulb className="w-3.5 h-3.5 text-tm-500" />
            Insights do período
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {computedInsights.map((ins, i) => {
              const color = ins.trend === "up" ? "text-tm-600" : ins.trend === "down" ? "text-danger-600" : "text-gray-500";
              return (
                <div key={i} className="card px-4 py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`${color}`}>
                      <TrendIcon trend={ins.trend} />
                    </span>
                    <p className="text-sm font-semibold text-gray-800">{ins.title}</p>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed mb-2">{ins.body}</p>
                  <p className={`text-lg font-bold ${color}`}>{ins.metric}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Gráficos ────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard
          title="Faturamento CT-e por Dia"
          subtitle={`${fmtPeriodLabel(filters.period)} — apenas CT-es autorizados`}
          height={240}
          loading={overviewLoading}
          isEmpty={dailyChartData.length === 0}
          emptyMessage="Sem CT-es autorizados no período selecionado."
        >
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dailyChartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-fat" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="var(--brand)" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="var(--brand)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tickFormatter={fmtDateShort}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false} axisLine={false}
              />
              <YAxis
                tickFormatter={(v) => v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : `${(v / 1_000).toFixed(0)}k`}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false} axisLine={false}
              />
              <Tooltip
                formatter={(v: number) => [fmtBRL(v), "Faturamento"]}
                labelFormatter={fmtDateShort}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
              />
              <Area
                type="monotone"
                dataKey="faturamento"
                stroke="var(--brand)"
                strokeWidth={2}
                fill="url(#grad-fat)"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Recebimentos × Pagamentos"
          subtitle="Últimos 5 meses — fluxo financeiro realizado"
          height={240}
          loading={finLoading}
          isEmpty={flowChartData.length === 0}
          emptyMessage="Sem dados de fluxo para exibir."
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={flowChartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false} axisLine={false}
              />
              <YAxis
                tickFormatter={(v) => v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : `${(v / 1_000).toFixed(0)}k`}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false} axisLine={false}
              />
              <Tooltip
                formatter={(v: number, name: string) => [fmtBRL(v), name === "receber" ? "Recebido" : "Pago"]}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
              />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                formatter={(n) => n === "receber" ? "Recebido" : "Pago"}
              />
              <Bar dataKey="receber" fill="var(--brand)" radius={[4, 4, 0, 0]} name="receber" />
              <Bar dataKey="pagar"   fill="#F59E0B" radius={[4, 4, 0, 0]} name="pagar" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ── Top Tomadores ──────────────────────────────────────────────────── */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">Top Tomadores por Faturamento</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Tomador de serviço (clientecon) · CT-es autorizados · {fmtPeriodLabel(filters.period)}
            </p>
          </div>
          {s && s.totalValue > 0 && (
            <span className="text-xs text-gray-500 hidden sm:block">
              Total: {fmtBRL(s.totalValue)}
            </span>
          )}
        </div>

        {overviewLoading && (
          <div className="p-6 text-sm text-gray-400 text-center">Carregando…</div>
        )}

        {!overviewLoading && (overview?.topCustomers ?? []).length === 0 && (
          <div className="p-6 text-sm text-gray-400 text-center">
            Sem dados de tomadores no período.
          </div>
        )}

        <div className="divide-y divide-surface-border">
          {customerList.visible.map((c, i) => {
            const total = overview!.topCustomers.reduce((a, x) => a + x.totalValue, 0);
            const sharePct = total > 0 ? (c.totalValue / total) * 100 : 0;
            return (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                <span className="text-xs font-bold text-gray-400 w-5 flex-shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{c.name}</p>
                  <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-tm-500 rounded-full transition-all"
                      style={{ width: `${Math.min(Math.max(sharePct, 2), 100)}%` }}
                    />
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-gray-900">{c.formatted}</p>
                  <p className="text-xs text-gray-400">
                    {sharePct.toFixed(1)}% · {fmtNumber(c.cteCount)} CT-es
                  </p>
                </div>
              </div>
            );
          })}
          <LoadMoreBar list={customerList} label="clientes" />
        </div>
      </div>

      {/* ── Maiores inadimplentes ──────────────────────────────────────────── */}
      {(fin?.topDebtors ?? []).length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <IconAlertTriangle className="w-4 h-4 text-danger-500" />
              Maiores Inadimplentes
            </p>
            <span className="text-xs text-gray-400">Vencido em aberto</span>
          </div>
          <div className="divide-y divide-surface-border">
            {debtorList.visible.map((d, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                <span className="text-xs font-bold text-gray-400 w-5 flex-shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{d.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">Mais antigo: {d.oldestDueDays} dias</p>
                </div>
                <p className="text-sm font-semibold text-danger-600 flex-shrink-0">{d.formatted}</p>
              </div>
            ))}
            <LoadMoreBar list={debtorList} label="devedores" />
          </div>
        </div>
      )}

      {/* ── Info de confiabilidade ─────────────────────────────────────────── */}
      <div className="flex items-center gap-2 text-xs text-gray-400 pb-2">
        <IconInfo className="w-3.5 h-3.5 flex-shrink-0" />
        <span>
          Faturamento e tomadores baseados em CT-es autorizados (status=2).
          Despesas operacionais não exibidas aqui — ver tela DRE.
          Contas a pagar podem estar parcialmente sincronizadas.
        </span>
      </div>

    </div>
  );
}
